import type { Auth, User } from "firebase/auth";
import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from "react";

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  // Keep Firebase's sign-in helper on the application origin in production.
  // This avoids third-party storage restrictions in Safari and other mobile browsers.
  authDomain: import.meta.env.PROD ? "kleanbg.com" : import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const googleClientId =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  "874903154626-1souf962p8ppog41rs8tgvco28qe9i15.apps.googleusercontent.com";

interface GoogleTokenResponse {
  access_token?: string;
  error?: string;
  error_description?: string;
}

interface GoogleTokenClient {
  requestAccessToken(options?: { prompt?: string }): void;
}

interface GoogleIdentityServices {
  accounts: {
    oauth2: {
      initTokenClient(config: {
        client_id: string;
        scope: string;
        callback(response: GoogleTokenResponse): void;
        error_callback?(error: { type?: string }): void;
      }): GoogleTokenClient;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

let googleScriptPromise: Promise<void> | null = null;

function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts.oauth2) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;

  const scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>("script[data-kleanbg-google-auth]");
    const loaded = () => {
      if (window.google?.accounts.oauth2) resolve();
      else reject(new Error("Google sign-in did not load correctly."));
    };
    const failed = () => reject(new Error("Google sign-in could not be loaded. Check your connection and try again."));

    if (existing) {
      existing.addEventListener("load", loaded, { once: true });
      existing.addEventListener("error", failed, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.dataset.kleanbgGoogleAuth = "true";
    script.addEventListener("load", loaded, { once: true });
    script.addEventListener("error", failed, { once: true });
    document.head.append(script);
  }).catch((error) => {
    googleScriptPromise = null;
    throw error;
  });
  googleScriptPromise = scriptPromise;

  return scriptPromise;
}

export async function requestGoogleAccessToken(): Promise<string> {
  await loadGoogleIdentityServices();

  return new Promise((resolve, reject) => {
    const client = window.google?.accounts.oauth2.initTokenClient({
      client_id: googleClientId,
      scope: "openid email profile",
      callback(response) {
        if (response.access_token) {
          resolve(response.access_token);
          return;
        }
        reject(new Error(response.error_description || "Google sign-in was cancelled."));
      },
      error_callback(error) {
        const message = error.type === "popup_closed"
          ? "Google sign-in was cancelled."
          : "Google sign-in could not open. Please allow pop-ups and try again.";
        reject(new Error(message));
      },
    });

    if (!client) {
      reject(new Error("Google sign-in is temporarily unavailable."));
      return;
    }
    client.requestAccessToken({ prompt: "select_account" });
  });
}

export const authConfigured = Object.values(config).every(Boolean);
type AuthModule = typeof import("firebase/auth");
let runtimePromise: Promise<{ auth: Auth; api: AuthModule }> | null = null;

async function authRuntime(): Promise<{ auth: Auth; api: AuthModule }> {
  if (!authConfigured) throw new Error("Authentication is not configured yet.");
  if (!runtimePromise) {
    runtimePromise = Promise.all([import("firebase/app"), import("firebase/auth")]).then(async ([appApi, authApi]) => {
      const app = appApi.getApps().length ? appApi.getApp() : appApi.initializeApp(config);
      const auth = authApi.getAuth(app);
      await authApi.setPersistence(auth, authApi.browserLocalPersistence);
      return { auth, api: authApi };
    });
  }
  return runtimePromise;
}

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signInGoogle(): Promise<void>;
  signInEmail(email: string, password: string): Promise<void>;
  register(email: string, password: string): Promise<void>;
  resendVerification(): Promise<void>;
  logout(): Promise<void>;
  token(): Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(authConfigured);

  useEffect(() => {
    if (!authConfigured) return;
    // Warm Google's small identity client before the user clicks. This keeps
    // the eventual account chooser directly attached to the click gesture.
    void loadGoogleIdentityServices().catch(() => undefined);
    let unsubscribe: (() => void) | undefined;
    void authRuntime().then(({ auth, api }) => {
      unsubscribe = api.onAuthStateChanged(auth, (nextUser) => {
        setUser(nextUser);
        setLoading(false);
      });
    }).catch(() => setLoading(false));
    return () => unsubscribe?.();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      async signInGoogle() {
        const [{ auth, api }, accessToken] = await Promise.all([authRuntime(), requestGoogleAccessToken()]);
        const credential = api.GoogleAuthProvider.credential(null, accessToken);
        await api.signInWithCredential(auth, credential);
      },
      async signInEmail(email, password) {
        const { auth, api } = await authRuntime();
        await api.signInWithEmailAndPassword(auth, email, password);
      },
      async register(email, password) {
        const { auth, api } = await authRuntime();
        const credential = await api.createUserWithEmailAndPassword(auth, email, password);
        await api.sendEmailVerification(credential.user);
      },
      async resendVerification() {
        if (!user || user.emailVerified) return;
        const { api } = await authRuntime();
        await api.sendEmailVerification(user);
      },
      async logout() {
        if (authConfigured) {
          const { auth, api } = await authRuntime();
          await api.signOut(auth);
        }
      },
      async token() {
        return user ? user.getIdToken() : null;
      },
    }),
    [user, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
